import { DEFAULT_CURRENCY } from '@app/common/constants';
import Text from '@app/components/ui/Text/Text';
import GlassCard from '@app/features/billing/components/BaseGlassCard/GlassCard';
import { APP_PREFIX, CUSTOMER_PROFILE_PAGE } from '@app/routes/urls';
import { currencyFormatter, type SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { Button, Grid, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';
import { LuStethoscope } from 'react-icons/lu';
import { MdOutlineBusiness } from 'react-icons/md';
import { RiNurseFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import CardHeader from './CardHeader';
import { commonTableClassName } from './tableStyles';
import type { PartnerDebtRow, PartnerType } from './types';

type PartnersDebtsCardProps = {
  data: PartnerDebtRow[];
  totalDebt: number;
  currency?: SupportedCurrencies;
  locale: string;
  isLoading?: boolean;
  onViewAllClick?: () => void;
  className?: string;
};

const PARTNER_TYPE_ICONS: Record<PartnerType, ReactNode> = {
  doctor: <LuStethoscope size={16} color="#94a3b8" />,
  nurse: <RiNurseFill size={16} color="#7dd3fc" />,
  provider: <MdOutlineBusiness size={16} color="#7c3aed" />,
};

// The customer-profile `type` param for each partner type.
const PROFILE_TYPE_BY_PARTNER_TYPE: Record<PartnerType, string> = {
  doctor: 'doctor',
  nurse: 'caregiver',
  provider: 'provider',
};

const PartnersDebtsCard = ({
  data,
  totalDebt,
  currency = DEFAULT_CURRENCY,
  locale,
  isLoading = false,
  onViewAllClick,
  className = 'col-span-12 lg:col-span-6 p-0',
}: PartnersDebtsCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const shouldShowNameTooltip = Boolean(screens.xxl);

  const handleProfileClick = (partnerId: string | number, partnerType: PartnerType) => {
    navigate(
      `/${APP_PREFIX}/${CUSTOMER_PROFILE_PAGE}?id=${partnerId}&type=${PROFILE_TYPE_BY_PARTNER_TYPE[partnerType]}`
    );
  };

  const columns: ColumnsType<PartnerDebtRow> = [
    {
      title: <Text type="label2" transKey="partner-capital" fw={700} noWrap />,
      dataIndex: 'partnerName',
      key: 'partnerName',
      render: (value: string) => {
        const nameCell = (
          <div className="min-w-0 max-w-full">
            <Text type="body2" untranslatedText={value} noWrap isTruncated className="block" />
          </div>
        );

        if (!shouldShowNameTooltip) {
          return nameCell;
        }

        return <Tooltip title={value}>{nameCell}</Tooltip>;
      },
    },
    {
      title: <Text type="label2" transKey="debt-capital" fw={700} noWrap />,
      dataIndex: 'debt',
      key: 'debt',
      align: 'right',
      width: 104,
      render: (value: number) => (
        <Text type="body2" untranslatedText={currencyFormatter(Math.abs(value), currency, locale)} color="#0f172a" />
      ),
    },
    {
      title: <Text type="label2" transKey="type-capital" fw={700} noWrap />,
      dataIndex: 'type',
      key: 'type',
      align: 'center',
      width: 76,
      render: (value: PartnerType) => (
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white">
          {PARTNER_TYPE_ICONS[value]}
        </span>
      ),
    },
    {
      title: <Text type="label2" transKey="last-payment-capital" fw={700} noWrap />,
      dataIndex: 'lastPaymentDate',
      key: 'lastPaymentDate',
      align: 'center',
      // Wide enough for DD/MM/YYYY once the cell's 32px of padding is taken off: at
      // 88px, sized when the date was DD/MM, the year broke onto a second line as
      // "29/07/2" / "026", and at 110px it left 78px for a string needing 80.
      width: 124,
      render: (value: string) => <Text type="body2" untranslatedText={value} color="#64748b" noWrap />,
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 50,
      render: (_, record) => (
        <Tooltip title={t('ledgers')}>
          <Button
            size="small"
            type="text"
            className="h-8 w-8 p-0 text-slate-500 hover:!text-slate-700 !bg-transparent !shadow-none"
            onClick={() => handleProfileClick(record.partnerId, record.type)}
            aria-label={t('ledgers')}
          >
            <FiExternalLink size={16} />
          </Button>
        </Tooltip>
      ),
    },
  ];

  const tableData = data.slice(0, 5);

  return (
    <GlassCard
      theme="balance"
      className={className}
      isLoading={isLoading}
      title={<CardHeader title={t('partners-you-owe')} onViewAllClick={onViewAllClick} />}
      titleClassName="w-full"
      bodyClassName="px-0 pb-5"
      body={
        <div className="text-base font-normal">
          <div className={commonTableClassName}>
            <Table<PartnerDebtRow>
              columns={columns}
              dataSource={tableData}
              pagination={false}
              size="small"
              tableLayout="fixed"
              footer={() => (
                <div className="flex items-center justify-between border-t border-slate-200 px-3 py-3 sm:px-4">
                  <Text type="label1" transKey="total" color="#0f172a" />

                  <Text
                    type="label1"
                    untranslatedText={currencyFormatter(Math.abs(totalDebt), currency, locale)}
                    color="#0f172a"
                  />
                </div>
              )}
            />
          </div>
        </div>
      }
    />
  );
};

export default PartnersDebtsCard;
